/**
 * Emma's animation engine: Brian's rig, driven by the app.
 *
 * The rig files (sphere.js, hat.js, geo.json) are Brian's, UNMODIFIED, as his
 * handoff asks. They are plain scripts rather than modules, so they are loaded
 * as raw text and evaluated in a sealed scope that hands back only what the
 * engine needs. Nothing leaks onto window.
 *
 * How she works (from the rig README): one body contour, an invisible ball
 * inside it with two eyes painted on, and two tables. The app never plays a
 * clip; it names an ADDRESS (front, engaged, thinking, sleepy...) and the rig
 * eases the ball there. The body only breathes, sleeps and wobbles. Everything
 * expressive is the eyes.
 *
 * This file is the state machine the README describes, rewritten in the app's
 * idiom as the README asks, rather than a port of touch3.js's fake phone.
 */
import sphereSrc from '@/vendor/emma/sphere.js?raw'
import hatSrc from '@/vendor/emma/hat.js?raw'
import GEO from '@/vendor/emma/geo.json'
import { placeCowboyHat } from './cowboyHat'

type Pt = [number, number]
interface Head { yaw: number; pitch: number; roll: number; gain: number; open: number; mod?: Record<string, number> | null }
interface Rig {
  renderEyes: (P: Pt[], H: Head, table: unknown[]) => {
    L: Pt[]; R: Pt[]; cx: number; cy: number; rad: number
    ridge: { fill: string; shade: string; c: number } | null
  }
  pathOfXY: (P: Pt[]) => string
  clampHead: <T extends { yaw: number; pitch: number }>(h: T) => T
  aimAt: (dx: number, dy: number, D: number) => { yaw: number; pitch: number }
  lerp: (a: number, b: number, t: number) => number
  SHAPE: unknown[]
  ADDR: Record<string, { yaw: number; pitch: number; gain: number }>
  SPH: { BREATH_NOD: number }
  placeHat: (g: Element, P: Pt[], E: Head, on: boolean, lift?: number, scale?: number, dx?: number, tilt?: number, openW?: number) => void
  hatSvg: (id: string) => string
}

let rig: Rig | null = null
function loadRig(): Rig {
  if (rig) return rig
  // eslint-disable-next-line no-new-func
  rig = new Function(
    `${sphereSrc}\n${hatSrc}\nreturn { renderEyes, pathOfXY, clampHead, aimAt, lerp, SHAPE, ADDR, SPH, placeHat, hatSvg };`,
  )() as Rig
  return rig
}

export type EmmaSkin = 'seed' | 'cowboy'

/** Curated colours. Her face is two black shapes on a flat fill, so the only
 *  rules are contrast with black eyes and separation from the dark app ground.
 *  Near-black or muddy picks would make her vanish, so there is no free picker. */
export const EMMA_COLORS: { id: string; label: string; hex: string }[] = [
  { id: 'amber',  label: 'Brand amber', hex: '#c8854a' },
  { id: 'wheat',  label: 'Wheat',       hex: '#e2b15b' },
  { id: 'sage',   label: 'Sage',        hex: '#8fb487' },
  { id: 'sky',    label: 'Sky',         hex: '#86b3d6' },
  { id: 'rose',   label: 'Rose',        hex: '#e3968c' },
  { id: 'lilac',  label: 'Lilac',       hex: '#b7a2dc' },
]

const BASE_Y = GEO.BASE_Y as number
const BORED_MS = 1600
/** Expose as config, per the README. */
export const DORMANT_MS = 20_000
const SETTLE_MS = 1350
const BREATH_MS = 3600
const WAKE_MS = 2300
const CURIOUS_MS = 1150
const AIM_D = 240
const TYPE_CYCLE = 3000
const TYPE_SCAN = 14

const easeIO = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2)
const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)
const breathS = (ph: number) => (ph < 0.4 ? easeIO(ph / 0.4) : ph < 0.9 ? 1 - easeIO((ph - 0.4) / 0.5) : 0)
const blinkAt = (t: number, a: number, b: number) => (t > a && t < b ? Math.sin((Math.PI * (t - a)) / (b - a)) : 0)
const lerpN = (a: number, b: number, t: number) => a + (b - a) * t

function lerpPts(A: Pt[], B: Pt[], t: number): Pt[] {
  return A.map((p, i) => [lerpN(p[0], B[i][0], t), lerpN(p[1], B[i][1], t)])
}
function jellyPts(P: Pt[], m: number): Pt[] {
  let mn = 1e9, mx = -1e9
  for (const p of P) { if (p[0] < mn) mn = p[0]; if (p[0] > mx) mx = p[0] }
  const cx = (mn + mx) / 2, kx = 1 / Math.pow(m, 0.7)
  return P.map((p) => [cx + (p[0] - cx) * kx, BASE_Y - (BASE_Y - p[1]) * m])
}
function pathOf(P: Pt[]): string {
  let s = `M${P[0][0].toFixed(1)} ${P[0][1].toFixed(1)}`
  for (let i = 1; i < P.length; i++) s += `L${P[i][0].toFixed(1)} ${P[i][1].toFixed(1)}`
  return `${s}Z`
}

function wakePose(t: number, fromPitch: number) {
  if (t >= WAKE_MS) return null
  if (t < 280) { const u = easeOut(t / 280); return { yaw: 0, pitch: lerpN(fromPitch, 3, u), gain: lerpN(0.9, 1.42, u), open: u } }
  if (t < 560) { const u = easeIO((t - 280) / 280); return { yaw: 0, pitch: lerpN(3, 0, u), gain: lerpN(1.42, 1, u), open: 1 - u * 0.97 } }
  if (t < 1460) { const u = (t - 560) / 900; return { yaw: 36 * Math.exp(-2 * u) * Math.sin(2 * Math.PI * 3.6 * u), pitch: 0, gain: 1, open: 0.03 } }
  const open = (t < 1560 ? easeIO((t - 1460) / 100) : 1) - Math.max(blinkAt(t, 1700, 1810), blinkAt(t, 1940, 2050))
  return { yaw: 0, pitch: 0, gain: 1, open: Math.max(0, open) }
}
function typeBeat(t: number) {
  const i = Math.floor(t / TYPE_CYCLE), u = t - i * TYPE_CYCLE
  const a = 1700 + (i % 3) * 250, b = a + 650 + (i % 2) * 200
  return u > a && u < b ? 'peek' : 'keyboard'
}
const typeScan = (t: number) => TYPE_SCAN * (0.72 * Math.sin(t / 1450) + 0.28 * Math.sin(t / 610 + 1.1))
const typeDrop = (t: number) => { const u = t % TYPE_CYCLE; return u < 320 ? 1 + 0.1 * Math.sin((Math.PI * u) / 320) : 1 }
const typeRoll = (t: number) => (Math.floor(t / TYPE_CYCLE) % 2 ? -1 : 1) * 6

type State = 'awake' | 'tracking' | 'bored' | 'dormant' | 'settling' | 'asleep' | 'waking'
  | 'curious' | 'typing' | 'thinking' | 'answering' | 'engaged'

export interface EmmaEls {
  svg: SVGSVGElement
  body: SVGPathElement
  over: SVGGElement
  eyeL: SVGPathElement
  eyeR: SVGPathElement
  clip: SVGPathElement
  ridge: SVGPathElement
  shade: SVGPathElement
  hat: SVGGElement
}

export class EmmaEngine {
  private r = loadRig()
  private els: EmmaEls
  private skin: EmmaSkin = 'seed'
  private NEUT: Pt[] = GEO.neutral as Pt[]
  private S_IN: Pt[] = GEO.sleep_in as Pt[]
  private S_OUT: Pt[] = GEO.sleep_out as Pt[]
  private hatArgs: [number, number, number, number, number] = [6, 1.25, 0, 0, 1]
  private overlays: { fill: string; neutral: Pt[] }[] = []

  private E: Head = { yaw: 0, pitch: 0, roll: 0, gain: 1, open: 1 }
  private T = { yaw: 0, pitch: 0, gain: 1 }
  private openT = 1
  private rollT = 0
  private cur = 0
  private curT = 0
  private curSide = 1
  private state: State = 'awake'
  /** Oct 22 (Brian: "she should move slightly … awake and alert"): resting
   *  alert, she never dozes off; left alone she glances about now and then. */
  private restAlert = false
  private nextGlance = performance.now() + 6000
  private lastP: Pt[]
  private prev = performance.now()
  private lastMove = 0
  private boredAt = performance.now()
  private settleStart = 0
  private sleepStart = 0
  private wakeStart = 0
  private wakeFrom: Pt[]
  private wakePitch0 = 0
  private wakeThen: null | (() => void) = null
  private typeStart = 0
  private nextIdleBlink = performance.now() + 1800
  private jelly: null | { t0: number; amp: number; freq: number; damp: number; len: number } = null
  private timers: number[] = []
  private raf = 0
  private blinkTimer = 0
  private stopped = false
  private reduced: boolean

  constructor(els: EmmaEls, opts: { skin?: EmmaSkin; reducedMotion?: boolean } = {}) {
    this.els = els
    this.reduced = !!opts.reducedMotion
    this.lastP = this.NEUT
    this.wakeFrom = this.NEUT
    this.setSkin(opts.skin ?? 'seed')
    this.raf = requestAnimationFrame(this.frame)
  }

  destroy() {
    this.stopped = true
    cancelAnimationFrame(this.raf)
    this.timers.forEach((t) => clearTimeout(t))
    clearTimeout(this.blinkTimer)
  }

  setSkin(name: EmmaSkin) {
    this.skin = name
    const s = (GEO.skins as Record<string, Record<string, unknown>>)[name]
    if (name === 'cowboy' && s) {
      this.NEUT = s.neutral as Pt[]; this.S_IN = s.sleep_in as Pt[]; this.S_OUT = s.sleep_out as Pt[]
      this.hatArgs = [(s.hatLift as number) ?? 6, (s.hatScale as number) ?? 1.25, (s.hatDx as number) ?? 0,
                      (s.hatTilt as number) ?? 0, (s.hatOpen as number) ?? 1]
      const band = (GEO.accessories as unknown as Record<string, { fill: string; refW: number; pts: Pt[] }>).bandana
      const cfg = s.bandana as { on: boolean; dy: number; scale: number } | undefined
      this.overlays = []
      if (band && cfg?.on) {
        let x0 = 1e9, x1 = -1e9
        for (const q of this.NEUT) { if (q[0] < x0) x0 = q[0]; if (q[0] > x1) x1 = q[0] }
        const k = ((x1 - x0) / band.refW) * (cfg.scale ?? 1)
        this.overlays.push({ fill: band.fill, neutral: band.pts.map((q) => [256 + (q[0] - 256) * k, 470 + (q[1] - 470) * k + (cfg.dy ?? 0)]) })
      }
    } else {
      this.NEUT = GEO.neutral as Pt[]; this.S_IN = GEO.sleep_in as Pt[]; this.S_OUT = GEO.sleep_out as Pt[]
      this.hatArgs = [6, 1.25, 0, 0, 1]
      this.overlays = []
    }
  }

  // ---------------- public events: the app speaks in addresses ----------------

  /** A tap anywhere on screen. She looks at it, head cocked, far eye narrower. */
  tapAt(clientX: number, clientY: number) {
    if (this.state === 'asleep' || this.state === 'settling' || this.state === 'waking') return
    if (this.busy()) return
    const b = this.els.svg.getBoundingClientRect()
    const ex = b.left + b.width / 2, ey = b.top + b.height * 0.45
    const a = this.r.aimAt(clientX - ex, clientY - ey, AIM_D)
    this.curSide = clientX < ex ? -1 : 1
    this.T = this.r.clampHead({ yaw: a.yaw, pitch: a.pitch, gain: 1.12 })
    this.curT = 1
    this.state = 'curious'
    this.later(CURIOUS_MS, () => {
      if (this.state !== 'curious') return
      this.curT = 0
      this.blink(90)
      this.goTo('front')
      this.state = 'awake'
      this.boredAt = performance.now()
    })
  }

  /** The feed moved. Pulling down she glances up-left at it, pushing up down-left. */
  scroll(direction: 'up' | 'down') {
    if (this.state === 'asleep' || this.state === 'settling' || this.state === 'waking' || this.busy()) return
    this.lastMove = performance.now()
    this.state = 'tracking'
    this.goTo(direction === 'down' ? 'upleft' : 'downleft', true)
  }

  lookUp() {
    if (this.state === 'asleep' || this.busy()) return
    this.lastMove = performance.now()
    this.state = 'tracking'
    this.goTo('lookup')
  }

  /** Tapped directly. Asleep, this is the one thing that wakes her. */
  poke(): boolean {
    if (this.state === 'asleep' || this.state === 'settling') { this.startWake(); return true }
    return false
  }

  /** Rest alert (never asleep) or let her doze after twenty seconds. */
  setRestAlert(on: boolean) {
    this.restAlert = on
    if (on && (this.state === 'asleep' || this.state === 'settling')) this.startWake()
  }

  /** She asks the screen's question: a beat of talking, then she waits on you. */
  ask() {
    const go = () => {
      this.state = 'answering'
      this.goTo('answering')
      this.jelly = { t0: performance.now(), amp: 0.1, freq: 3, damp: 5, len: 600 }
      this.later(2200, () => { if (this.state === 'answering') { this.state = 'engaged'; this.goTo('engaged') } })
    }
    if (this.state === 'asleep' || this.state === 'settling') { this.wakeThen = go; this.startWake() } else go()
  }

  chatOpened() {
    const go = () => { this.state = 'engaged'; this.goTo('engaged') }
    if (this.state === 'asleep' || this.state === 'settling') { this.wakeThen = go; this.startWake() } else go()
  }

  chatClosed() {
    this.state = 'awake'
    this.goTo('front')
    this.boredAt = performance.now()
  }

  /** Keyboard open: parked on the keys, glancing up at you every few seconds. */
  typing() {
    const go = () => { if (this.state !== 'typing') { this.state = 'typing'; this.typeStart = performance.now() } }
    if (this.state === 'asleep' || this.state === 'settling') { this.wakeThen = go; this.startWake() } else go()
  }

  typingStopped() {
    if (this.state === 'typing') { this.state = 'engaged'; this.goTo('engaged') }
  }

  /** Message sent: front, then thinking until the reply lands. */
  sent() {
    this.state = 'thinking'
    this.blink(90)
    this.goTo('front')
    this.later(60, () => { if (this.state === 'thinking') this.goTo('thinking') })
  }

  /** The reply arrived: answering, with a wobble, then back to engaged. */
  answered() {
    this.state = 'answering'
    this.goTo('answering')
    this.jelly = { t0: performance.now(), amp: 0.12, freq: 3.2, damp: 5, len: 600 }
    this.later(1800, () => { if (this.state === 'answering') { this.state = 'engaged'; this.goTo('engaged') } })
  }

  // ---------------- internals ----------------

  private busy() {
    return this.state === 'typing' || this.state === 'thinking' || this.state === 'answering' || this.state === 'engaged'
  }

  private later(ms: number, fn: () => void) {
    this.timers.push(window.setTimeout(fn, ms))
  }

  private goTo(name: string, jitter = false) {
    const a = this.r.ADDR[name] ?? this.r.ADDR.front
    const o = { yaw: a.yaw, pitch: a.pitch, gain: a.gain }
    if (jitter) { o.yaw += (Math.random() - 0.5) * 8; o.pitch += (Math.random() - 0.5) * 6; o.gain *= 1 + (Math.random() - 0.5) * 0.16 }
    this.T = this.r.clampHead(o)
  }

  private blink(ms = 90) {
    clearTimeout(this.blinkTimer)
    this.openT = 0
    this.blinkTimer = window.setTimeout(() => { this.openT = 1 }, ms)
  }

  private startSettle(now: number) {
    this.state = 'settling'
    this.settleStart = now
    this.openT = 0
    this.goTo('sleepy')
  }

  /** Never straight from sleep into a stare: startle, squint to slits, a
   *  decaying head shake, open, two blinks, land. */
  private startWake() {
    const now = performance.now()
    this.state = 'waking'
    this.wakeStart = now
    this.wakeFrom = this.lastP
    this.wakePitch0 = this.E.pitch
    clearTimeout(this.blinkTimer)
    this.jelly = { t0: now + 100, amp: 0.24, freq: 3.4, damp: 5.5, len: 750 }
  }

  private frame = (now: number) => {
    if (this.stopped) return
    const r = this.r
    const dt = Math.min(50, now - this.prev)
    this.prev = now
    const kg = 1 - Math.exp(-dt / 110)
    const ko = 1 - Math.exp(-dt / (this.openT < this.E.open ? 38 : 70))
    const E = this.E, T = this.T

    if (this.reduced) {
      // Static: she holds her address and does not breathe, blink or wobble.
      E.yaw = T.yaw; E.pitch = T.pitch; E.gain = T.gain; E.open = 1
      this.draw(this.NEUT)
      this.raf = requestAnimationFrame(this.frame)
      return
    }

    E.yaw = r.lerp(E.yaw, T.yaw, kg)
    E.pitch = r.lerp(E.pitch, T.pitch, kg)
    E.gain = r.lerp(E.gain, T.gain, kg)
    E.open = r.lerp(E.open, this.openT, ko)

    let P = this.NEUT
    if (this.state === 'settling') {
      const t = Math.min(1, (now - this.settleStart) / SETTLE_MS)
      E.open = 1 - easeIO(Math.min(1, (now - this.settleStart) / (SETTLE_MS * 0.85)))
      P = jellyPts(lerpPts(this.NEUT, this.S_IN, easeIO(t)), lerpN(1, 0.955, easeIO(t)))
      if (t >= 1) { this.state = 'asleep'; this.sleepStart = now }
    } else if (this.state === 'asleep') {
      const s = breathS(((now - this.sleepStart) % BREATH_MS) / BREATH_MS)
      P = jellyPts(lerpPts(this.S_IN, this.S_OUT, s), 0.955)
      T.pitch = r.ADDR.sleepy.pitch + r.SPH.BREATH_NOD * s
    } else if (this.state === 'waking') {
      const t = Math.min(1, (now - this.wakeStart) / 160)
      P = lerpPts(this.wakeFrom, this.NEUT, easeIO(t))
      const w = wakePose(now - this.wakeStart, this.wakePitch0)
      if (w) {
        E.yaw = w.yaw; E.pitch = w.pitch; E.gain = w.gain; E.open = w.open
        this.T = { yaw: w.yaw, pitch: w.pitch, gain: w.gain }; this.openT = w.open
      } else {
        this.goTo('front'); this.state = 'awake'; this.boredAt = now; this.nextIdleBlink = now + 2200
        const then = this.wakeThen; this.wakeThen = null; then?.()
      }
    }

    if (this.state === 'typing') {
      const e = now - this.typeStart, n = typeBeat(e)
      this.goTo(n)
      if (n === 'keyboard') { this.T.yaw = r.ADDR.keyboard.yaw + typeScan(e); this.T.gain = r.ADDR.keyboard.gain * typeDrop(e); this.rollT = 0 }
      else this.rollT = typeRoll(e)
    } else if (this.state === 'curious') this.rollT = this.curSide * 7
    else this.rollT = 0

    E.roll = r.lerp(E.roll, this.rollT, kg)
    this.cur = r.lerp(this.cur, this.curT, kg)
    E.mod = this.cur > 0.01
      ? { aL: this.curSide > 0 ? -0.18 * this.cur : 0, aR: this.curSide < 0 ? -0.18 * this.cur : 0,
          tl: this.curSide * 5 * this.cur, tr: this.curSide * 5 * this.cur, brow: -0.06 * this.cur }
      : null

    if (this.jelly) {
      const u = (now - this.jelly.t0) / this.jelly.len
      if (u >= 0 && u <= 1) P = jellyPts(P, 1 + Math.exp(-this.jelly.damp * u) * Math.cos(2 * Math.PI * this.jelly.freq * u) * this.jelly.amp)
      else if (u > 1) this.jelly = null
    }

    this.draw(P)
    this.lastP = P

    // Interest decays on MOTION, not on touch release; then, left alone, she
    // dozes off. Anything busy (chat, typing, answering) holds her awake.
    if (this.state === 'tracking' && now - this.lastMove > BORED_MS) {
      this.state = 'bored'; this.boredAt = now; this.blink(110)
      this.later(700, () => { if (this.state === 'bored' || this.state === 'dormant') this.goTo('front') })
    }
    if (this.state === 'bored' || this.state === 'awake' || this.state === 'dormant') {
      if (this.state === 'bored' && now - this.boredAt > 800) this.state = 'dormant'
      if (this.restAlert) {
        if (now > this.nextGlance && this.state !== 'bored') {
          this.goTo(Math.random() < 0.5 ? 'upleft' : 'downleft', true)
          this.later(1300, () => { if (this.state === 'awake' || this.state === 'dormant') { this.blink(90); this.goTo('front') } })
          this.nextGlance = now + 7000 + Math.random() * 6000
        }
      } else if (now - this.boredAt > DORMANT_MS) this.startSettle(now)
      if (now > this.nextIdleBlink) { this.blink(90); this.nextIdleBlink = now + 2600 + Math.random() * 2600 }
    }
    this.raf = requestAnimationFrame(this.frame)
  }

  private draw(P: Pt[]) {
    const r = this.r
    const d = pathOf(P)
    this.els.body.setAttribute('d', d)
    this.els.clip.setAttribute('d', d)
    const out = r.renderEyes(P, this.E, r.SHAPE)
    this.els.eyeL.setAttribute('d', r.pathOfXY(out.L))
    this.els.eyeR.setAttribute('d', r.pathOfXY(out.R))
    const rg = out.ridge
    this.els.ridge.setAttribute('d', rg ? rg.fill : '')
    this.els.shade.setAttribute('d', rg ? rg.shade : '')
    if (rg) this.els.shade.setAttribute('stroke-opacity', (0.16 * rg.c).toFixed(2))
    // Overlays (the bandana) ride the body's squash via a base-anchored affine.
    if (this.overlays.length) {
      let nx0 = 1e9, nx1 = -1e9, ny0 = 1e9, px0 = 1e9, px1 = -1e9, py0 = 1e9
      for (const q of this.NEUT) { if (q[0] < nx0) nx0 = q[0]; if (q[0] > nx1) nx1 = q[0]; if (q[1] < ny0) ny0 = q[1] }
      for (const q of P) { if (q[0] < px0) px0 = q[0]; if (q[0] > px1) px1 = q[0]; if (q[1] < py0) py0 = q[1] }
      const sx = (px1 - px0) / (nx1 - nx0), sy = (470 - py0) / (470 - ny0), ncx = (nx0 + nx1) / 2, pcx = (px0 + px1) / 2
      this.els.over.innerHTML = this.overlays
        .map((o) => `<path d="${pathOf(o.neutral.map((q) => [(q[0] - ncx) * sx + pcx, (q[1] - 470) * sy + 470] as Pt))}" fill="${o.fill}"/>`)
        .join('')
    } else if (this.els.over.innerHTML) {
      this.els.over.innerHTML = ''
    }
    placeCowboyHat(this.els.hat, P, this.E, this.skin === 'cowboy', ...this.hatArgs)
  }
}

/** A tiny app-wide bus, so any screen can nudge Emma without prop drilling. */
type Listener = (e: EmmaEvent) => void
export type EmmaEvent =
  | { type: 'scroll'; direction: 'up' | 'down' }
  | { type: 'lookup' }
  | { type: 'open' }
const listeners = new Set<Listener>()
export const emmaBus = {
  emit(e: EmmaEvent) { listeners.forEach((l) => l(e)) },
  on(l: Listener) { listeners.add(l); return () => { listeners.delete(l) } },
}
